"use client";

import Image from "next/image";
import { Award, Heart, Star, ChevronRight } from "lucide-react";
import { useState } from "react";
import Link from "next/link";
import type { Hospital } from "@/data/mockData";
import { useI18n } from "@/i18n/I18nProvider";

interface HospitalCardProps {
  hospital: Hospital;
}

function HospitalCard({ hospital }: HospitalCardProps) {
  const [liked, setLiked] = useState(false);
  const { t } = useI18n();

  return (
    <Link
      href={`/hospitals/${hospital.id}`}
      className="block bg-white rounded-[14px] overflow-hidden shadow-[0_2px_10px_rgba(14,60,58,0.08)] active:scale-[0.98] transition-transform"
    >
      <div className="relative h-[82px] bg-primary-bg">
        <Image
          src={hospital.image}
          alt={hospital.name}
          fill
          sizes="(max-width: 430px) 50vw, 200px"
          className="object-cover"
        />
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setLiked(!liked);
          }}
          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white shadow-[0_1px_4px_rgba(0,0,0,0.16)] flex items-center justify-center"
          aria-label={liked ? t("Remove from favorites") : t("Add to favorites")}
        >
          <Heart
            size={12}
            className={liked ? "fill-red-500 text-red-500" : "text-text-dark/35"}
          />
        </button>
        {hospital.isTop && (
          <span className="absolute left-2 bottom-2 inline-flex items-center gap-1 rounded-full bg-amber-400 px-1.5 py-0.5 text-[10.5px] font-bold text-white shadow-sm">
            <Award size={10} strokeWidth={2.4} />
            {t("Top Hospital")}
          </span>
        )}
      </div>

      <div className="px-3 pt-2 pb-2.5">
        <h4 className="text-[14px] font-bold leading-[1.3] text-text-dark">
          {hospital.name}
        </h4>
        <p className="mt-1 text-[12px] leading-[1.45] text-text-dark/55">
          {hospital.city} &nbsp;|&nbsp; {hospital.specialties.slice(0, 2).join(", ")}
        </p>
        <div className="mt-1.5 flex items-center gap-1 min-w-0">
          <Star size={11} className="shrink-0 fill-amber-400 text-amber-400" />
          <span className="text-[12px] font-semibold text-text-dark">{hospital.rating}</span>
          <span className="text-[11px] text-text-dark/45 truncate">
            ({hospital.reviews.toLocaleString()} {t("reviews")})
          </span>
        </div>
      </div>
    </Link>
  );
}

interface HospitalCardsProps {
  hospitals: Hospital[];
}

export default function HospitalCards({ hospitals }: HospitalCardsProps) {
  const { t } = useI18n();
  return (
    <section className="mt-3 px-5">
      <div className="flex items-center justify-between mb-1.5">
        <h3 className="text-[17px] font-bold text-text-dark tracking-[-0.2px]">
          {t("Recommended Hospitals")}
        </h3>
        <Link
          href="/hospitals"
          className="flex items-center gap-0.5 text-[12px] font-semibold text-primary"
        >
          {t("View All")}
          <ChevronRight size={13} />
        </Link>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {hospitals.slice(0, 2).map((hospital) => (
          <HospitalCard key={hospital.id} hospital={hospital} />
        ))}
      </div>
    </section>
  );
}
